import { useId, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { DeviceGenre } from '@/constants/device';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { cn } from '@/lib/cn';
import IconButton from '../Button/IconButton';
import DeviceCatalogCombobox, { type DeviceCatalogInfo } from '../Combobox/DeviceCatalogCombobox';
import type { UseDeviceCatalogsForSelect } from '../Combobox/DeviceCatalogCombobox/-query';
import Icon from '../Icon/Icon';
import { Button } from '../ui/button';
type Props = {
  value?: DeviceCatalogInfo;
  onChange: (catalog?: DeviceCatalogInfo) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  placeHolder?: string;
  width?: number;
  /**
   * カスタム useQuery Hook（依存性注入）
   * Individual 画面など、異なる API エンドポイントを使用する場合に指定
   */
  useQuery?: UseDeviceCatalogsForSelect;
  /** 紐付け対象デバイスのジャンル。 */
  genre?: DeviceGenre;
  /** trueの場合、ジャンル未確定時は候補を開かずqueryも実行しない。 */
  requireGenre?: boolean;
};

/**
 * DeviceCatalog の選択肢に対応した Select
 */
export function DeviceCatalogSelect(props: Props) {
  const tc = useCommonTranslation();
  const [open, setOpen] = useState(false);
  const popoverContentId = useId();
  const canClear = Boolean(props.allowEmpty && !props.disabled && props.value);
  const candidateQueryEnabled = !props.requireGenre || Boolean(props.genre);
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && !candidateQueryEnabled) return;
    setOpen(nextOpen);
  };
  return <Popover open={open} onOpenChange={handleOpenChange}>
      <div className="relative inline-block" style={props.width ? {
      width: props.width
    } : undefined}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} aria-controls={popoverContentId} className={cn('relative w-full justify-start overflow-hidden text-ellipsis pr-10', !props.value && 'text-muted-foreground', !candidateQueryEnabled && 'cursor-not-allowed opacity-50')} aria-disabled={!candidateQueryEnabled || undefined} disabled={props.disabled}>
            <span className={cn('min-w-0 truncate', canClear && 'pr-8')}>
              {props.value?.name || props.placeHolder || tc('pleaseSelect')}
            </span>
            <div className="absolute inset-y-0 right-3 flex items-center">
              <Icon iconName="ChevronDownIcon" className="shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>

        {canClear && <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton icon="XIcon" variant="mutedGhost" size="ExtraSmall" aria-label="Clear selection" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          props.onChange(undefined);
        }} onPointerDown={e => {
          e.preventDefault();
          e.stopPropagation();
        }} />
          </div>}
      </div>
      <PopoverContent id={popoverContentId} className="w-full overflow-hidden p-0" style={props.width ? {
      width: props.width
    } : undefined} align="start">
        <DeviceCatalogCombobox onSelect={catalog => {
        props.onChange(catalog);
        setOpen(false);
      }} onClose={() => setOpen(false)} useQuery={props.useQuery} genre={props.genre} queryEnabled={candidateQueryEnabled} />
      </PopoverContent>
    </Popover>;
}